<template>
	<div class="runReportRoot" role="dialog" aria-labelledby="runReportTitle">
		<div class="titleBar">
			<div id="runReportTitle" class="title" role="alert">Run report<template v-if="subtitle"> — {{subtitle}}</template></div>
			<div class="mutedText runIdText">run <code class="inline">{{runId}}</code></div>
		</div>
		<div class="dialogBody">
			<div v-if="loading" class="loadingRow"><ScaleLoader /> Loading the report…</div>
			<div v-else-if="notAvailable" class="notAvailable">Run reports are not available yet in this build.  Server: {{error}}</div>
			<div v-else-if="error" class="errorBox">{{error}}</div>
			<div v-else-if="!html" class="notAvailable">The server returned an empty report.</div>
			<!-- The report is server-generated HTML (DbMigrate's BuildHtmlReport plus the orchestrator summary).  It is rendered
			     inside a fully sandboxed iframe (no scripts, no same-origin access, no forms, no navigation) rather than injected
			     into the page. -->
			<iframe v-else class="reportFrame" sandbox="" referrerpolicy="no-referrer" :srcdoc="html" title="Maintenance run report"></iframe>
		</div>
		<div class="footer">
			<div ref="closeBtn" class="dialogButton" role="button" tabindex="0" @click="DefaultClose" @keydown.space.enter.prevent="DefaultClose">Close</div>
		</div>
	</div>
</template>

<script>
	// Shows a finished run's HTML report in a sandboxed iframe.  Fetches the report itself so the dialog opens immediately.
	import { GetDbMaintenanceReport } from 'appRoot/api/DbMaintenance';
	import { isNotAvailableYet } from './runFormat';

	export default {
		props:
		{
			runId: {
				type: String,
				required: true
			},
			/** Optional caption (e.g. the run kind and start time). */
			subtitle: {
				type: String,
				default: null
			}
		},
		data()
		{
			return {
				loading: true,
				html: null,
				error: null
			};
		},
		computed:
		{
			notAvailable()
			{
				return isNotAvailableYet(this.error);
			}
		},
		created()
		{
			GetDbMaintenanceReport(this.runId)
				.then(result =>
				{
					if (result.error)
					{
						this.error = result.error;
						return;
					}
					this.html = typeof result.html === 'string' ? result.html : null;
				})
				.catch(ex =>
				{
					this.error = ex.message || String(ex);
				})
				.finally(() =>
				{
					this.loading = false;
				});
		},
		methods:
		{
			SetFocus()
			{
				if (this.$refs.closeBtn)
					this.$refs.closeBtn.focus();
			},
			DefaultClose()
			{
				this.$emit('close', false);
			}
		}
	}
</script>

<style scoped>
	.runReportRoot
	{
		background-color: #FFFFFF;
		width: min(1100px, calc(100vw - 30px));
		height: calc(100vh - 40px);
		display: flex;
		flex-direction: column;
	}

	.titleBar
	{
		padding: 12px 16px 8px 16px;
		border-bottom: 1px solid #E0E0E0;
		flex: 0 0 auto;
	}

	.title
	{
		text-align: center;
		font-weight: bold;
		font-size: 16pt;
	}

	.runIdText
	{
		text-align: center;
		font-size: 0.85em;
	}

	.mutedText
	{
		color: #888888;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

	.dialogBody
	{
		flex: 1 1 auto;
		min-height: 0;
		display: flex;
		flex-direction: column;
		padding: 8px 16px;
	}

	.reportFrame
	{
		flex: 1 1 auto;
		width: 100%;
		border: 1px solid #DDDDDD;
		background-color: #FFFFFF;
	}

	.loadingRow
	{
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 20px;
	}

	.notAvailable
	{
		background-color: #F4F4F4;
		border: 1px solid #DDDDDD;
		color: #555555;
		padding: 12px;
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		white-space: pre-wrap;
	}

	.footer
	{
		flex: 0 0 auto;
		border-top: 1px solid #E0E0E0;
		display: flex;
		justify-content: flex-end;
		padding: 8px 16px;
	}

	.dialogButton
	{
		display: inline-block;
		cursor: pointer;
		font-weight: bold;
		font-size: 12pt;
		padding: 10px 22px;
		border-radius: 5px;
		color: #4A9E42;
		border: 1px solid #DDDDDD;
	}

		.dialogButton:hover,
		.dialogButton:focus
		{
			background-color: rgba(0,0,0,0.05);
		}
</style>
